/**
 * ToastProvider — Context-based imperative toast API
 * @layer atom
 * @package @plerity/ui-core
 *
 * Thin shim over `@plerity/toast`: the provider owns a PRIVATE store instance
 * (so two providers never show each other's toasts, exactly as before) and
 * renders it through the ui-core ToastContainer. The store owns ids, timers and
 * the cap. For toasts raised outside React (`toast.error(...)` from a query
 * callback), mount `<ToastHost />` once instead.
 *
 * Usage:
 *   // Wrap once near the root of your React island or app:
 *   <ToastProvider position="top-right">
 *     <YourApp />
 *   </ToastProvider>
 *
 *   // In any descendant component:
 *   const toast = useToast();
 *   toast.success("File uploaded");
 *   toast.error("Upload failed", { durationMs: 0 }); // sticky
 */

import { createToastStore } from "@plerity/toast";
import { WebToastHost } from "@plerity/toast/web";
import { createContext, useContext, useEffect, useMemo } from "react";
import { ToastContainer } from "./Toast";
import type { ToastContextValue, ToastProviderProps } from "./Toast.types";

const QUEUE_CAP = 5;

export const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children, maxToasts = QUEUE_CAP, position = "bottom-right" }: ToastProviderProps) {
  const store = useMemo(() => createToastStore({ max: maxToasts }), [maxToasts]);

  // Cancel pending auto-dismiss timers when the provider (or a changed cap) goes away.
  useEffect(() => () => store.destroy(), [store]);

  // `store.api` is referentially stable for the store's life, so `useToast()` returns a STABLE
  // reference across renders. A fresh object per render once looped the DAM AssetLibraryIsland
  // load effect at ~50 fetches/s (2026-08-13: error -> toast.error -> re-render -> new `toast`
  // -> new `loadAssets` -> effect re-fired -> fetch -> error -> ...). Do not wrap it in a new object.
  const value: ToastContextValue = store.api;

  return (
    <ToastContext.Provider value={value}>
      {children}
      <WebToastHost Container={ToastContainer} store={store} position={position} />
    </ToastContext.Provider>
  );
}

/**
 * useToast — access the imperative toast API from any component inside <ToastProvider>.
 *
 * @throws if called outside of a <ToastProvider> tree.
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error(
      "useToast() must be called inside a <ToastProvider>. " +
        "Wrap your component tree (or React island root) with <ToastProvider>."
    );
  }
  return ctx;
}
